/*
 * Application shell.
 *
 * The rail is the only thing above the router: it holds the base list, which every
 * screen needs, so opening a base is not a reload of the list. Everything else hangs
 * off the parsed route, and the route carries the tab so a reviewer can be linked
 * straight to a diff.
 */
import { useCallback, useEffect, useState } from 'react'
import { setUnauthorizedHandler } from './api/client.ts'
import { listKnowledgeBases, type KnowledgeBase } from './api/knowledgeBases.ts'
import { useSession } from './auth/useSession.ts'
import { navigate, parseRoute, useAppPath, workbenchPath, type TabId } from './lib/router.ts'
import { CreateDialog } from './features/knowledgeBases/CreateDialog.tsx'
import { ListScreen } from './features/knowledgeBases/ListScreen.tsx'
import { Workbench } from './features/knowledgeBases/Workbench.tsx'
import { AsyncState } from './features/knowledgeBases/components/AsyncState.tsx'
import { useResource } from './features/knowledgeBases/lib/useResource.ts'
import { LoginScreen } from './screens/LoginScreen.tsx'
import { num } from './features/knowledgeBases/lib/format.ts'

export function App() {
  const session = useSession()
  const path = useAppPath()
  const route = parseRoute(path)
  const [includeArchived, setIncludeArchived] = useState(false)
  const [creating, setCreating] = useState(false)

  // One place handles a dead session, rather than every panel threading a callback.
  useEffect(() => {
    setUnauthorizedHandler(session.expire)
    return () => setUnauthorizedHandler(null)
  }, [session.expire])

  const authenticated = session.status === 'authenticated'
  const bases = useResource<{ items: KnowledgeBase[]; total: number }>(
    `bases:${authenticated}:${includeArchived}`,
    () =>
      authenticated
        ? listKnowledgeBases(includeArchived)
        : Promise.resolve({ items: [], total: 0 }),
  )

  const reloadBases = useCallback(() => bases.reload(), [bases])

  if (session.status === 'loading') {
    return (
      <main className="login">
        <p className="muted">Загружаем…</p>
      </main>
    )
  }

  if (session.status === 'anonymous') {
    return <LoginScreen error={session.error} onSubmit={session.signIn} />
  }

  const items = bases.data?.items ?? []
  const activeId = route.name === 'workbench' ? route.kbId : null

  return (
    <div className="shell">
      <nav className="rail">
        <div className="rail__brand">
          <span className="rail__mark" aria-hidden="true">
            ◈
          </span>
          Базы знаний
        </div>

        <button
          type="button"
          className="rail__item"
          aria-current={route.name === 'list'}
          onClick={() => navigate('/')}
        >
          <span className="ic" aria-hidden="true">
            ☰
          </span>
          <span className="label">Все базы</span>
          <span className="cnt">{bases.data ? num(bases.data.total) : ''}</span>
        </button>

        <div className="rail__group kicker">Базы</div>

        {bases.status === 'loading' ? (
          <div style={{ padding: '6px 9px', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[0, 1, 2].map((slot) => (
              <span className="skeleton" key={slot} style={{ width: `${88 - slot * 14}%` }} />
            ))}
          </div>
        ) : items.length === 0 ? (
          <p className="field__hint" style={{ padding: '2px 9px' }}>
            Пока ни одной базы.
          </p>
        ) : (
          items.map((base) => (
            <button
              key={base.id}
              type="button"
              className="rail__item"
              aria-current={base.id === activeId}
              title={base.name}
              onClick={() => navigate(workbenchPath(base.id))}
            >
              <span className="ic" aria-hidden="true">
                {base.id === activeId ? '◉' : '◎'}
              </span>
              <span className="label">{base.name}</span>
              <span className="cnt">{num(base.raw_files ?? 0)}</span>
            </button>
          ))
        )}

        <button type="button" className="rail__new" onClick={() => setCreating(true)}>
          + Создать базу знаний
          {bases.data ? <small>{num(bases.data.total)} уже есть</small> : null}
        </button>

        <div className="rail__foot">
          <div className="rail__who" title={session.user?.email ?? ''}>
            {session.user?.email}
          </div>
          {session.scopeLabel ? <div className="rail__who">скоуп: {session.scopeLabel}</div> : null}
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => void session.signOut()}
          >
            Выйти
          </button>
        </div>
      </nav>

      {route.name === 'workbench' ? (
        <Workbench
          kbId={route.kbId}
          tab={route.tab}
          onTab={(tab: TabId) => navigate(workbenchPath(route.kbId, tab))}
          onBaseChanged={reloadBases}
        />
      ) : (
        <div className="wb">
          <div className="wb__body">
            {bases.status === 'ready' ? (
              <ListScreen
                bases={items}
                includeArchived={includeArchived}
                onIncludeArchived={setIncludeArchived}
                onOpen={(kbId) => navigate(workbenchPath(kbId))}
                onCreate={() => setCreating(true)}
              />
            ) : (
              <div className="pane">
                <AsyncState
                  what="список баз"
                  status={bases.status}
                  error={bases.error}
                  httpStatus={bases.httpStatus}
                  onRetry={bases.reload}
                  skeletonRows={4}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {creating ? (
        <CreateDialog
          onClose={() => setCreating(false)}
          onCreated={(base) => {
            setCreating(false)
            reloadBases()
            navigate(workbenchPath(base.id))
          }}
        />
      ) : null}
    </div>
  )
}
